import { useTranslation } from 'react-i18next';
import { HostBridgeProvider, useHostBridge } from './context/HostBridgeContext';
import { useChatPermissions } from './hooks/useChatPermissions';
import { ChatErrorBoundary } from './components/ErrorBoundary';
import { ChatPage } from './components/ChatPage';
import { GateState } from './components/GateState';
import { DevModePanel } from './components/DevModePanel';

// Developer mode: in dev builds, when opened standalone (not embedded in a host
// iframe) and not auto-bootstrapped from env vars, show a sign-in panel instead
// of waiting forever for a host handshake. Never present in production builds.
const DEV_MODE_AVAILABLE = import.meta.env.DEV && !import.meta.env.VITE_DEV_TOKEN;

function AppShell() {
  const { t } = useTranslation();
  const { status, session, error, isStandalone } = useHostBridge();
  const { canUse } = useChatPermissions();

  if (status === 'waiting' || !session) {
    if (DEV_MODE_AVAILABLE && isStandalone && status === 'waiting') {
      return <DevModePanel />;
    }
    return (
      <GateState
        icon="creation"
        spinning
        title={t('ChatbotUI.WaitingForHost')}
        body={t('ChatbotUI.WaitingForHostHint')}
      />
    );
  }

  if (status === 'error') {
    return (
      <GateState
        icon="alert-circle"
        variant="error"
        title={t('ChatbotUI.ErrorTitle')}
        body={error ?? ''}
      />
    );
  }

  if (!canUse) {
    return (
      <GateState
        icon="shield-check-outline"
        title={t('ChatbotUI.NoPermissionTitle')}
        body={t('ChatbotUI.NoPermissionBody')}
      />
    );
  }

  return <ChatPage session={session} />;
}

export function App() {
  return (
    <ChatErrorBoundary>
      <HostBridgeProvider>
        <AppShell />
      </HostBridgeProvider>
    </ChatErrorBoundary>
  );
}
